import type { TReviewLineItem } from "./createViewFileReview";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";

/** Subset of line item field metadata used for client-side validation. */
export type TLineItemFieldDefForValidation = {
    key: keyof TReviewLineItem;
    label: string;
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
};

export const SEGMENT_TYPES_WITH_ALLOWED_LIST = [2, 4, 5, 6, 7] as const;
export type TSegmentTypeWithAllowedList = (typeof SEGMENT_TYPES_WITH_ALLOWED_LIST)[number];

/** Maps segment type from `getSegmentAllowedValuesBatch` to form state keys. */
export const SEGMENT_TYPE_TO_FORM_KEY: Record<TSegmentTypeWithAllowedList, keyof TReviewLineItem> =
    {
        2: "mappedSegment2",
        4: "mappedSegment4",
        5: "mappedSegment5",
        6: "mappedSegment6",
        7: "mappedSegment7",
    };

export type TValidateLineEditFormParams = {
    form: Partial<TReviewLineItem>;
    fields: TLineItemFieldDefForValidation[];
    segmentAllowedValues: Record<number, string[]>;
};

/**
 * Client-side validation for the file review line edit modal.
 * Mirrors a subset of backend rules (required mapped account/propcode, allowed segment values).
 */
export function validateLineEditForm(
    params: TValidateLineEditFormParams,
): Partial<Record<keyof TReviewLineItem, string>> {
    const errors: Partial<Record<keyof TReviewLineItem, string>> = {};
    const { form, fields, segmentAllowedValues } = params;

    for (const field of fields) {
        if (!field.required || field.alwaysReadOnly) continue;
        const val = form[field.key];
        if (val === null || val === undefined || String(val).trim() === "") {
            errors[field.key] = `${field.label} is required`;
        }
    }

    // Note: propcode is selection-only via SearchableSelect; no "valid propcode" list
    // check is needed here — only the required check above applies.

    for (const segType of SEGMENT_TYPES_WITH_ALLOWED_LIST) {
        const formKey = SEGMENT_TYPE_TO_FORM_KEY[segType];
        const allowed = segmentAllowedValues[segType];
        const val = form[formKey];
        if (allowed && allowed.length > 0 && val != null && String(val).trim() !== "") {
            if (!allowed.includes(String(val))) {
                errors[formKey] = "Value is not allowed for this account and propcode";
            }
        }
    }

    return errors;
}

/** Maps backend segment index (1–7) to the line edit form field we surface in the modal. */
export function segmentIndexToLineEditFormKey(segmentNum: number): keyof TReviewLineItem | null {
    switch (segmentNum) {
        case 1:
            return "segment1";
        case 2:
            return "mappedSegment2";
        case 3:
            return "segment3";
        case 4:
            return "mappedSegment4";
        case 5:
            return "mappedSegment5";
        case 6:
            return "mappedSegment6";
        case 7:
            return "mappedSegment7";
        default:
            return null;
    }
}

/** Strip optional `CODE: message` wrapper (some paths may still include it). */
function normalizeBackendLineMessage(raw: string): string {
    let t = raw.trim();
    const codeMatch = t.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/s);
    if (codeMatch) t = codeMatch[1]!.trim();
    return t;
}

/**
 * Maps backend line validation messages to edit-modal field keys so invalid rows
 * highlight the same areas as the table tooltip. Covers `FileValidationService` and
 * OTB row validators; segment N maps to the fields exposed in the line edit modal.
 */
export function mapBackendLineItemValidationErrorsToFields(
    validationErrors: string[],
): Partial<Record<keyof TReviewLineItem, string>> {
    const out: Partial<Record<keyof TReviewLineItem, string>> = {};

    const setMsg = (key: keyof TReviewLineItem, msg: string) => {
        const prev = out[key];
        out[key] = prev && prev !== msg ? `${prev}; ${msg}` : msg;
    };

    const assignSegmentFromMessage = (msg: string): boolean => {
        const segmentPatterns = [
            /Invalid segment (\d+) value/i,
            /Required segment (\d+) is missing/i,
            /requires segment (\d+)/i,
            /in segment (\d+)/i,
        ];
        for (const re of segmentPatterns) {
            const m = msg.match(re);
            if (m?.[1]) {
                const key = segmentIndexToLineEditFormKey(Number.parseInt(m[1], 10));
                if (key) {
                    setMsg(key, msg);
                    return true;
                }
            }
        }
        return false;
    };

    for (const raw of validationErrors) {
        const msg = normalizeBackendLineMessage(raw);
        if (!msg) continue;

        if (assignSegmentFromMessage(msg)) {
            continue;
        }

        if (
            /row must have debit\/credit or amount value/i.test(msg) ||
            /^AMOUNT is required/i.test(msg)
        ) {
            setMsg("amount", msg);
            continue;
        }

        if (/debit amount cannot be negative/i.test(msg)) {
            setMsg("debit", msg);
            continue;
        }

        if (/credit amount cannot be negative/i.test(msg)) {
            setMsg("credit", msg);
            continue;
        }

        if (/account number is required/i.test(msg)) {
            setMsg("breAccountNumber", msg);
            continue;
        }

        if (/^propcode is required/i.test(msg)) {
            setMsg("propcode", msg);
            continue;
        }

        if (/SOURCE_ACCOUNT_NUMBER is required/i.test(msg)) {
            setMsg("sourceAccountNumber", msg);
            continue;
        }

        if (/LOCAL_PROPERTY_CODE is required/i.test(msg)) {
            setMsg("localPropcode", msg);
            continue;
        }

        if (/property code not mapped|unmapped property/i.test(msg)) {
            setMsg("propcode", msg);
            setMsg("localPropcode", msg);
        }
    }

    return out;
}

export function mergeLineEditFieldErrors(params: {
    server: Partial<Record<keyof TReviewLineItem, string>>;
    client: Partial<Record<keyof TReviewLineItem, string>>;
    showClientErrors: boolean;
    editingLineItem: TReviewLineItem | null;
    editFormData: Partial<TReviewLineItem>;
}): Partial<Record<keyof TReviewLineItem, string>> {
    const { server, client, showClientErrors, editingLineItem, editFormData } = params;
    const merged: Partial<Record<keyof TReviewLineItem, string>> = { ...server };

    if (editingLineItem) {
        for (const k of Object.keys(merged) as (keyof TReviewLineItem)[]) {
            const init = editingLineItem[k];
            const cur = editFormData[k];
            if (String(init ?? "") !== String(cur ?? "")) {
                delete merged[k];
            }
        }
    }

    if (showClientErrors) {
        for (const k of Object.keys(client) as (keyof TReviewLineItem)[]) {
            const v = client[k];
            if (v) merged[k] = v;
        }
    }

    return merged;
}

/**
 * Apply a regular (non-account) field change to the form.
 */
export function applyLineItemFieldChange(
    prev: Partial<TReviewLineItem>,
    key: keyof TReviewLineItem,
    value: string | number,
): Partial<TReviewLineItem> {
    return { ...prev, [key]: value };
}

/**
 * Apply an account selection (from either the code or name SearchableSelect) to the form.
 * Selecting from either field sets BOTH breAccountNumber and breAccountName.
 * Passing null clears both fields.
 */
export function applyAccountSelection(
    prev: Partial<TReviewLineItem>,
    option: TSearchableOption | null,
): Partial<TReviewLineItem> {
    if (option === null) {
        return { ...prev, breAccountNumber: null, breAccountName: null };
    }
    const code = String(option["code"] ?? option.value);
    const name = String(option["name"] ?? option.label);
    return { ...prev, breAccountNumber: code, breAccountName: name };
}
